/**
 * Enhanced Chat Integration Hook
 * Integrates ChatSessionService with React state management
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { ChatSessionService, ChatMessage, WidgetSpec } from '../services/ChatSessionService';

interface UseChatEnhancedReturn {
  messages: ChatMessage[];
  statusMessage: string;
  isProcessing: boolean;
  sendMessage: (message: string, firmId?: string) => Promise<void>;
  clearMessages: () => void;
  sessionId: string | null;
}

export const useChatEnhanced = (): UseChatEnhancedReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const chatServiceRef = useRef<ChatSessionService>(new ChatSessionService());

  // Initialize session on mount
  useEffect(() => {
    const initSession = async () => {
      const sessionId = chatServiceRef.current.getSessionId();
      if (!sessionId) {
        try {
          await chatServiceRef.current.createSession();
        } catch (error) {
          console.error('Failed to create session:', error);
        }
      }
    };

    initSession();

    // Cleanup on unmount
    return () => {
      chatServiceRef.current.closeConnection();
    };
  }, []);

  const sendMessage = useCallback(async (message: string, firmId?: string) => {
    if (!message.trim()) return;

    setIsProcessing(true);
    setStatusMessage('');

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date(),
      isStreaming: false
    };

    setMessages(prev => [...prev, userMessage]);

    // Create assistant message placeholder
    const assistantId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      type: 'assistant',
      content: '',
      contentSegments: [],
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);

    try {
      await chatServiceRef.current.sendMessage(
        message,
        {
          onStatus: (msg, step) => {
            setStatusMessage(msg);
            // Update the streaming message's status
            setMessages(prev =>
              prev.map(message =>
                message.id === assistantId
                  ? { ...message, streamingStatus: msg }
                  : message
              )
            );
            console.log(`Status [${step}]:`, msg);
          },

          onTextChunk: (text) => {
            setMessages(prev =>
              prev.map(msg => {
                if (msg.id !== assistantId) return msg;
                
                const segments = [...(msg.contentSegments || [])];
                const lastSegmentIndex = segments.length - 1;
                const lastSegment = segments[lastSegmentIndex];
                
                // If last segment is text, create new segment with appended text
                if (lastSegment && lastSegment.type === 'text') {
                  segments[lastSegmentIndex] = {
                    type: 'text' as const,
                    content: lastSegment.content + text
                  };
                } else {
                  // Otherwise, create a new text segment
                  segments.push({ type: 'text' as const, content: text });
                }
                
                return {
                  ...msg,
                  contentSegments: segments,
                  streamingStatus: undefined // Clear status when text starts arriving
                };
              })
            );
          },

          onWidget: (widget: WidgetSpec) => {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantId
                  ? {
                      ...msg,
                      contentSegments: [
                        ...(msg.contentSegments || []),
                        { type: 'widget' as const, widget }
                      ]
                    }
                  : msg
              )
            );
          },

          onComplete: (metadata) => {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantId
                  ? { ...msg, isStreaming: false, streamingStatus: undefined }
                  : msg
              )
            );
            setStatusMessage('');
            setIsProcessing(false);
            console.log('Completed:', metadata);
          },

          onError: (error) => {
            console.error('Chat error:', error);
            setMessages(prev => prev.filter(msg => msg.id !== assistantId));
            
            // Add error message
            const errorMessage: ChatMessage = {
              id: Date.now().toString(),
              type: 'assistant',
              content: 'Sorry, I encountered an error. Please try again.',
              timestamp: new Date(),
              isStreaming: false
            };
            setMessages(prev => [...prev, errorMessage]);
            
            setStatusMessage('');
            setIsProcessing(false);
          }
        },
        firmId
      );
    } catch (error) {
      console.error('Send message error:', error);
      setStatusMessage('');
      setIsProcessing(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStatusMessage('');
  }, []);

  return {
    messages,
    statusMessage,
    isProcessing,
    sendMessage,
    clearMessages,
    sessionId: chatServiceRef.current.getSessionId()
  };
};
